import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { RootState } from "../../../app/store";

import FraudElementObservationCard from "./FraudElementObservationCard";
import FraudElementsSidebar from "./FraudElementSidebar";

import { selectFraudElementsForParty } from "../logic/fraudElementsSlice";

import FraudElementDetailsCard from "./FraudElementsDetailsCard";
import EmptyFraudElementsState from "./EmptyFraudElementState";
import { FraudElementContributors } from "../../../api/fraudElements";
import {
  getFraudElementContributors,
  isGenerateFraudElementsEnabled,
  isAIEnabled,
} from "../../configs/logic/Slice";
import AIFraudElementsContainer from "./AIFraudElementsContainer";

interface FraudElementsContentProps {
  selectedPartyId?: string;
}

export default function FraudElementsContent({
  selectedPartyId,
}: FraudElementsContentProps) {
  const fraudElements = useSelector(
    (state: RootState) =>
      selectFraudElementsForParty(state, selectedPartyId),
    shallowEqual,
  );

  const fraudElementContributors = useSelector(getFraudElementContributors) as FraudElementContributors[];

  const [selectedFraudElementId, setSelectedFraudElementId] = useState<string>();

  const isGenerateFraudElementsFFEnabled = useSelector(isGenerateFraudElementsEnabled);
  const isAIFFEnabled = useSelector(isAIEnabled);

  const isAIGenerationEnabled = isGenerateFraudElementsFFEnabled && isAIFFEnabled;

  useEffect(() => {
    const isSelectionValid = fraudElements.some(
      (fraud) => fraud.fraud_element_id === selectedFraudElementId
    );

    if (!isSelectionValid) {
      setSelectedFraudElementId(fraudElements[0]?.fraud_element_id);
    }
  }, [fraudElements, selectedFraudElementId]);

  const selectedFraudElement = useMemo(
    () =>
      fraudElements.find(
        (fraud) => fraud.fraud_element_id === selectedFraudElementId,
      ),
    [fraudElements, selectedFraudElementId],
  );

  if (!fraudElements.length) {
    return isAIGenerationEnabled && selectedPartyId ? (
      <AIFraudElementsContainer
        key={selectedPartyId}
        partyId={selectedPartyId}
      />
    ) : (
      <EmptyFraudElementsState />
    );
  }

  if (!selectedFraudElement) {
    return null;
  }

  return (
    <div className="flex items-start gap-4 rounded-b-[14px] bg-white px-4 py-4">
      <FraudElementsSidebar
        partyId={selectedPartyId!}
        selectedFraudElementId={selectedFraudElementId}
        onFraudElementChange={setSelectedFraudElementId}
      />

      <div className="flex flex-1 flex-col gap-6">
        <FraudElementDetailsCard
          fraudElementId={selectedFraudElement.fraud_element_id}
        />

        {fraudElementContributors.map((contributor, index) => (
          <FraudElementObservationCard
            key={contributor}
            fraudElementId={selectedFraudElement.fraud_element_id}
            contributor={contributor}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}